import { Alert, Button, Group, Text } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import type { PortalReport } from "@/api/portal";
import { reissuePortalReportPin, type PortalPinReissue } from "@/api/pinReissue";
import { config } from "@/config";
import { t, type MessageKey } from "@/i18n";
import { messages } from "@/messages.en";

/**
 * "Send a new PIN" on a released report (Gate B M3 S21, report-summary PR #151 section 5.4).
 *
 * Shown only when onboarding-api has the route (`config.portalPinReissueEnabled`) and only
 * on a released report. Asks first, because the old PIN stops
 * working at once. The server decides who may act and records it as `report.pin_reissued`.
 *
 * The server's text is shown only when it is exactly one of the orchestrator's fixed refusal
 * sentences (the `portal.pin_reissue_refused_*` messages), otherwise a fixed sentence per
 * status, as everywhere on this page.
 */
export function PinReissueAction({ report }: { report: PortalReport }): JSX.Element | null {
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<PortalPinReissue | null>(null);
  const reissue = useMutation({
    mutationFn: () => reissuePortalReportPin(report.report_id),
    onSuccess: (result) => {
      setDone(result);
      setConfirming(false);
    },
    onError: () => setConfirming(false),
  });

  // A report a correction replaced is refused by the server with its own sentence: the
  // portal projection does not carry the replacement.
  if (!config.portalPinReissueEnabled || report.review_state !== "released") return null;

  return (
    <>
      {!confirming ? (
        <Group mt="md" data-testid="pin-reissue">
          <Button
            variant="outline"
            onClick={() => {
              setDone(null);
              setConfirming(true);
            }}
            disabled={reissue.isPending}
          >
            {t("portal.pin_reissue")}
          </Button>
        </Group>
      ) : (
        <Alert color="blue" mt="md" data-testid="pin-reissue-confirm">
          <Text size="sm">{t("portal.pin_reissue_confirm")}</Text>
          <Group mt="sm">
            <Button loading={reissue.isPending} onClick={() => reissue.mutate()}>
              {t("portal.report_confirm")}
            </Button>
            <Button
              variant="default"
              disabled={reissue.isPending}
              onClick={() => setConfirming(false)}
            >
              {t("portal.report_cancel")}
            </Button>
          </Group>
        </Alert>
      )}
      {done !== null ? (
        <Alert
          color={done.email_sent ? "green" : "orange"}
          mt="md"
          data-testid="pin-reissue-done"
        >
          {!done.email_sent
            ? t("portal.pin_reissue_not_emailed")
            : t(done.new_link ? "portal.pin_reissue_new_link_done" : "portal.pin_reissue_done").replace(
                "{days}",
                String(done.days_remaining),
              )}
        </Alert>
      ) : null}
      {reissue.isError ? (
        <Alert color="red" mt="md" data-testid="pin-reissue-error">
          {pinReissueErrorText(reissue.error)}
        </Alert>
      ) : null}
    </>
  );
}

const REFUSED_PREFIX = "portal.pin_reissue_refused_";
const REFUSAL_SENTENCES: ReadonlySet<string> = new Set(
  Object.entries(messages)
    .filter(([key]) => key.startsWith(REFUSED_PREFIX))
    .map(([, text]) => text),
);

/** The sentence to show for a failed reissue. Exported for its test. */
export function pinReissueErrorText(error: unknown): string {
  const message = error instanceof Error ? error.message : "";
  const match = /^API request failed: (\d{3})[^—]*(?:— (.*))?$/s.exec(message);
  const status = match?.[1];
  const reason = match?.[2]?.trim();
  if ((status === "409" || status === "503") && reason && REFUSAL_SENTENCES.has(reason)) {
    return reason;
  }
  const key: MessageKey =
    status === "409" || status === "404"
      ? "portal.pin_reissue_conflict"
      : status === "403"
        ? "portal.pin_reissue_forbidden"
        : "portal.pin_reissue_unavailable";
  return t(key);
}
